import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListFavoriteCollections,
  useListCollectionsForRecipe,
  useAddRecipeToFavoriteCollection,
  useRemoveRecipeFromFavoriteCollection,
  useCreateFavoriteCollection,
  getListCollectionsForRecipeQueryKey,
  getListFavoriteCollectionsQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { FolderPlus, Plus, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface CollectionPickerProps {
  recipeId: number;
  /** "pill" — labelled button suitable for a recipe detail header.
   *  "icon" — compact circular button suitable for a card overlay. */
  variant?: "pill" | "icon";
  className?: string;
}

export function CollectionPicker({
  recipeId,
  variant = "pill",
  className,
}: CollectionPickerProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: collectionsData, isLoading: loadingCollections } =
    useListFavoriteCollections({ query: { enabled: open } });
  const { data: memberData, isLoading: loadingMembers } =
    useListCollectionsForRecipe(recipeId, { query: { enabled: open } });

  const collections = collectionsData?.collections ?? [];
  const memberSet = useMemo(
    () => new Set(memberData?.collectionIds ?? []),
    [memberData],
  );

  const invalidateAll = () => {
    void qc.invalidateQueries({
      queryKey: getListCollectionsForRecipeQueryKey(recipeId),
    });
    void qc.invalidateQueries({
      queryKey: getListFavoriteCollectionsQueryKey(),
    });
    // Detail pages may be open for any collection — invalidate broadly.
    void qc.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        q.queryKey[0].startsWith("/api/favorite-collections"),
    });
  };

  const addMut = useAddRecipeToFavoriteCollection({
    mutation: { onSettled: invalidateAll },
  });
  const removeMut = useRemoveRecipeFromFavoriteCollection({
    mutation: { onSettled: invalidateAll },
  });
  const createMut = useCreateFavoriteCollection({
    mutation: {
      onSuccess: async (created) => {
        invalidateAll();
        // Auto-add the recipe to the just-created collection.
        await addMut.mutateAsync({ collectionId: created.id, recipeId });
        setNewName("");
      },
      onError: () =>
        toast({
          title: "Could not create collection",
          variant: "destructive",
        }),
    },
  });

  const toggle = (collectionId: number, currentlyMember: boolean) => {
    if (currentlyMember) {
      removeMut.mutate({ collectionId, recipeId });
    } else {
      addMut.mutate({ collectionId, recipeId });
    }
  };

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || name.length > 100) return;
    createMut.mutate({ data: { name } });
  };

  const trigger =
    variant === "pill" ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn("gap-1.5 h-8 text-sm", className)}
        title="Add to a collection"
      >
        <FolderPlus className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Collections</span>
      </Button>
    ) : (
      <button
        type="button"
        title="Add to a collection"
        aria-label="Add to a collection"
        className={cn(
          "inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/85 border border-white/50 backdrop-blur-sm shadow-sm text-muted-foreground hover:text-primary hover:bg-white transition-all",
          className,
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FolderPlus className="w-4 h-4" />
      </button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b">
          <p className="text-sm font-semibold">Add to collection</p>
          <p className="text-xs text-muted-foreground">
            Group this recipe by occasion or theme.
          </p>
        </div>

        <div className="max-h-56 overflow-y-auto py-1">
          {loadingCollections || loadingMembers ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Loading…
            </div>
          ) : collections.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No collections yet — create your first one below.
            </div>
          ) : (
            collections.map((c) => {
              const isMember = memberSet.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/60 cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={isMember}
                    onCheckedChange={() => toggle(c.id, isMember)}
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {c.recipeCount}
                  </span>
                </label>
              );
            })
          )}
        </div>

        <form onSubmit={submitNew} className="p-3 border-t flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New collection name"
            maxLength={100}
            className="h-8 text-sm"
          />
          <Button
            type="submit"
            size="sm"
            className="h-8 px-2"
            disabled={createMut.isPending || newName.trim().length === 0}
          >
            {createMut.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
